/**
 * DRBFM API Service
 *
 * Handles all DRBFM-related API calls including:
 * - Session management (CRUD)
 * - Analysis execution (SSE streaming)
 * - Results retrieval
 * - HTML export
 */

import type { TFunction } from 'i18next';
import { api, uploadFile } from './api';
import { fetchWithSession } from './fetchWithSession';
import { analyzeRequestBody, analysisFailureMessage } from './drbfm.analysis';
import { failureModeToLegacyPayload, mapLegacyConversion } from './evidence.mappers';
import type { LegacyConversion } from '../types/evidence';
import { translateWithFallback, type TranslationFallbackOptions } from '../utils/translation';
import {
  DRBFMSession,
  CreateSessionResponse,
  SessionListResponse,
  SessionResultsResponse,
  AnalyzeRequest,
  ConvertToVerificationRequest,
  ConvertToVerificationResponse,
  DesignChange,
  FailureMode,
  Countermeasure,
  DRBFMCase,
  DRBFMCaseDesignChange,
  DRBFMCaseInputData,
  DRBFMCasePrecheckItem,
  DRBFMStage,
  DRBFMStageRun,
  DRBFMWorkflowArtifact,
  DRBFMWorkflowResponse,
  ConfigureWorkflowRequest,
  CreateWorkflowArtifactRequest,
  RunWorkflowStageRequest,
  UpdateDesignChangeReviewRequest,
  UpdateFailureModeReviewRequest,
  FailureModeRevisionPreview,
  UpdateCountermeasureReviewRequest,
  DRBFMReadiness,
  FunctionPartMatrix,
  MatrixCellUpdateRequest,
} from '../types';
import {
  CreateSessionParams,
  ListSessionsParams,
  AnalysisCallbacks,
  LinkCadRequest,
  LinkCadResponse,
} from './drbfm.service.types';
import {
  firstPresent,
  mapSession,
  mapSessionDesignChange,
  mapWorkflowArtifact,
  mapStageRun,
  mapWorkflowResponse,
  mapFunctionPartMatrix,
  mapReadiness,
  mapFailureMode,
  mapCountermeasure,
  mapSessionResults,
  mapCase,
  mapCaseDesignChange,
  mapCasePrecheckItem,
  caseDesignChangeToRequest,
  precheckAnswerToRequest,
  mapWorkflowOptions,
  sanitizeFilename,
} from './drbfm.service.mappers';

// Re-export types and case mappers for existing public API consumers.
export type {
  CreateSessionParams,
  ListSessionsParams,
  AnalysisCallbacks,
  LinkCadRequest,
  LinkCadResponse,
};
export { mapCase, mapCaseDesignChange, mapCasePrecheckItem };

// ==============================================================================
// API Base URL
// ==============================================================================

const DRBFM_BASE_URL = '/drbfm';

// ==============================================================================
// Session Management
// ==============================================================================

/**
 * Create a new DRBFM session
 */
export const createSession = async (
  params: CreateSessionParams
): Promise<CreateSessionResponse> => {
  // Convert to snake_case for backend API
  const request = {
    item_name: params.itemName,
    title: params.title,
    spec_old_document_id: params.specOldDocumentId,
    spec_new_document_id: params.specNewDocumentId,
  };

  // Transform response back to camelCase
  const response = await api.post<any>(`${DRBFM_BASE_URL}/sessions`, request);
  return {
    id: response.id,
    itemName: response.item_name,
    title: response.title,
    status: response.status,
    currentFlow: response.current_flow,
    createdAt: response.created_at,
  };
};

/**
 * List DRBFM sessions with pagination
 */
export const listSessions = async (
  params: ListSessionsParams = {}
): Promise<SessionListResponse> => {
  const { limit = 20, offset = 0 } = params;

  const response = await api.get<any>(
    `${DRBFM_BASE_URL}/sessions?limit=${limit}&offset=${offset}`
  );

  // Map snake_case API response to camelCase DRBFMSession[]
  // Backend returns item_name/created_at/completed_at; UI reads itemName/createdAt/completedAt.
  return {
    sessions: (response.sessions || []).map(mapSession),
    total: response.total,
    limit: response.limit,
    offset: response.offset,
  };
};

/**
 * Get a specific DRBFM session by ID
 */
export const getSession = async (sessionId: string): Promise<DRBFMSession> => {
  // API returns SessionDetailResponse which maps to DRBFMSession
  const response = await api.get<any>(`${DRBFM_BASE_URL}/sessions/${sessionId}`);

  return mapSession(response);
};

/**
 * Get session results (design changes, failure modes, countermeasures)
 */
export const getSessionResults = async (
  sessionId: string
): Promise<SessionResultsResponse> => {
  const response = await api.get<any>(`${DRBFM_BASE_URL}/sessions/${sessionId}/results`);
  return mapSessionResults(response);
};

/**
 * Delete a DRBFM session
 */
export const deleteSession = async (sessionId: string): Promise<void> => {
  await api.delete(`${DRBFM_BASE_URL}/sessions/${sessionId}`);
};

/**
 * Link an existing DRBFM session's failure modes to a CAD model.
 */
export const linkCad = async (
  sessionId: string,
  request: LinkCadRequest,
): Promise<LinkCadResponse> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/sessions/${sessionId}/link-cad`, {
    cad_model_id: request.cadModelId,
    predefined_part_labels: request.predefinedPartLabels || [],
  });

  return {
    linkedCount: response.linked_count,
    unresolvedFailureModeIds: response.unresolved_failure_mode_ids || [],
  };
};

// ==============================================================================
// Staged Workflow
// ==============================================================================

export const getWorkflow = async (
  sessionId: string,
): Promise<DRBFMWorkflowResponse> => {
  const response = await api.get<any>(`${DRBFM_BASE_URL}/sessions/${sessionId}/workflow`);
  return mapWorkflowResponse(response);
};

export const configureWorkflow = async (
  sessionId: string,
  request: ConfigureWorkflowRequest,
): Promise<DRBFMWorkflowResponse> => {
  const response = await api.patch<any>(`${DRBFM_BASE_URL}/sessions/${sessionId}/workflow`, {
    workflow_phase: request.workflowPhase,
    input_pattern: request.inputPattern,
    change_granularity: request.changeGranularity,
    workflow_metadata: request.workflowMetadata || {},
  });
  return mapWorkflowResponse(response);
};

export const getFunctionPartMatrix = async (
  sessionId: string,
): Promise<FunctionPartMatrix> => {
  const response = await api.get<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/workflow/function-part`,
  );
  return mapFunctionPartMatrix(response)!;
};

export const importFunctionPartMatrix = async (
  sessionId: string,
  file: File,
): Promise<FunctionPartMatrix> => {
  const response = await uploadFile(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/workflow/function-part/import`,
    file,
  );
  return mapFunctionPartMatrix(response)!;
};

export const generateFunctionPartMatrix = async (
  sessionId: string,
): Promise<FunctionPartMatrix> => {
  const response = await api.post<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/workflow/function-part/generate-matrix`,
  );
  return mapFunctionPartMatrix(response)!;
};

export const prefillFunctionPartMatrix = async (
  sessionId: string,
  context: Record<string, unknown> = {},
): Promise<FunctionPartMatrix> => {
  const response = await api.post<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/workflow/function-part/prefill`,
    { context },
  );
  return mapFunctionPartMatrix(response)!;
};

export const updateFunctionPartMatrixCell = async (
  sessionId: string,
  cellId: string,
  request: MatrixCellUpdateRequest,
): Promise<FunctionPartMatrix> => {
  const response = await api.patch<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/workflow/function-part/cells/${cellId}`,
    {
      status: request.status,
      old_value: request.oldValue,
      new_value: request.newValue,
      change_summary: request.changeSummary,
      affected_requirement_ids: request.affectedRequirementIds,
      reviewer_note: request.reviewerNote,
    },
  );
  return mapFunctionPartMatrix(response)!;
};

export const promoteFunctionPartMatrix = async (
  sessionId: string,
): Promise<{ promotedChanges: DesignChange[]; workflow: DRBFMWorkflowResponse }> => {
  const response = await api.post<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/workflow/function-part/promote`,
  );
  return {
    promotedChanges: (
      response.promoted_changes ?? response.promotedChanges ?? []
    ).map(mapSessionDesignChange),
    workflow: mapWorkflowResponse(response.workflow ?? {}),
  };
};

export const createWorkflowArtifact = async (
  sessionId: string,
  request: CreateWorkflowArtifactRequest,
): Promise<DRBFMWorkflowArtifact> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/sessions/${sessionId}/artifacts`, {
    artifact_type: request.artifactType,
    source_type: request.sourceType,
    document_id: request.documentId,
    cad_model_id: request.cadModelId,
    title: request.title,
    payload: request.payload || {},
  });
  return mapWorkflowArtifact(response.artifact ?? response);
};

export const runWorkflowStage = async (
  sessionId: string,
  stage: DRBFMStage,
  request: RunWorkflowStageRequest = {},
): Promise<{ stageRun: DRBFMStageRun; workflow: DRBFMWorkflowResponse }> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/sessions/${sessionId}/stages/${stage}/run`, {
    input_artifact_ids: request.inputArtifactIds || [],
    options: mapWorkflowOptions(request.options || {}),
  });
  return {
    stageRun: mapStageRun(response.stage_run ?? response.stageRun),
    workflow: mapWorkflowResponse(response.workflow ?? {}),
  };
};

export const updateDesignChangeReview = async (
  sessionId: string,
  designChangeId: string,
  request: UpdateDesignChangeReviewRequest,
): Promise<DesignChange> => {
  const response = await api.patch<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/design-changes/${designChangeId}/review`,
    {
      review_status: request.reviewStatus,
      reviewer_note: request.reviewerNote,
      granularity: request.granularity,
      category: request.category,
      item_name: request.itemName,
      old_specification: request.oldSpecification,
      new_specification: request.newSpecification,
      change_description: request.changeDescription,
      impact_areas: request.impactAreas,
      source_evidence: request.sourceEvidence,
    },
  );
  return mapSessionDesignChange(response.design_change ?? response);
};

/** Records that a verbatim pair held only for comparability is a genuine specification change. */
export const confirmComparability = async (
  sessionId: string,
  designChangeId: string,
  rationale: string,
): Promise<DesignChange> => {
  const response = await api.post<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/design-changes/${designChangeId}/confirm-comparability`,
    { rationale },
  );
  return mapSessionDesignChange(response.design_change ?? response);
};

export const updateFailureModeReview = async (
  sessionId: string,
  failureModeId: string,
  request: UpdateFailureModeReviewRequest,
): Promise<FailureMode> => {
  const response = await api.patch<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/failure-modes/${failureModeId}/review`,
    {
      review_status: request.reviewStatus,
      failure_description: request.failureDescription,
      failure_cause: request.failureCause,
      failure_effect: request.failureEffect,
      severity: request.severity,
      severity_score: request.severityScore,
      occurrence_score: request.occurrenceScore,
      detection_score: request.detectionScore,
      score_status: request.scoreStatus,
      countermeasure_requirement: request.countermeasureRequirement,
      countermeasure_not_required_reason: request.countermeasureNotRequiredReason,
      causal_claims: request.causalClaims?.map((claim) => ({
        claim: claim.claim,
        evidence_level: claim.evidenceLevel,
        evidence: claim.evidence,
      })),
      unknown_assumptions: request.unknownAssumptions,
      reviewer_note: request.reviewerNote,
    },
  );
  return mapFailureMode(response.failure_mode ?? response);
};

export const previewFailureModeRevision = async (
  sessionId: string,
  failureModeId: string,
  instruction: string,
): Promise<FailureModeRevisionPreview> => {
  const response = await api.post<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/failure-modes/${failureModeId}/revision-preview`,
    { instruction },
  );
  const preview = response.preview ?? response;
  return {
    failureDescription: preview.failure_description ?? preview.failureDescription,
    failureCause: preview.failure_cause ?? preview.failureCause,
    failureEffect: preview.failure_effect ?? preview.failureEffect,
    decisionBasis: preview.decision_basis ?? preview.decisionBasis,
    unknownAssumptions: preview.unknown_assumptions ?? preview.unknownAssumptions ?? [],
  };
};

export const updateCountermeasureReview = async (
  sessionId: string,
  countermeasureId: string,
  request: UpdateCountermeasureReviewRequest,
): Promise<Countermeasure> => {
  const response = await api.patch<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/countermeasures/${countermeasureId}/review`,
    {
      review_status: request.reviewStatus,
      countermeasure_type: request.countermeasureType,
      description: request.description,
      target_cause: request.targetCause,
      mechanism: request.mechanism,
      verification_method: request.verificationMethod,
      acceptance_criteria: request.acceptanceCriteria,
      prerequisites: request.prerequisites,
      unknown_assumptions: request.unknownAssumptions,
      validated_effectiveness_score: request.validatedEffectivenessScore,
      feasibility_score: request.feasibilityScore,
      priority_class: request.priorityClass,
      priority_basis: request.priorityBasis,
      reviewer_note: request.reviewerNote,
    },
  );
  return mapCountermeasure(response.countermeasure ?? response);
};

export const getReadiness = async (sessionId: string): Promise<DRBFMReadiness> => {
  const response = await api.get<any>(`${DRBFM_BASE_URL}/sessions/${sessionId}/readiness`);
  return mapReadiness(response);
};

export const createReviewRevision = async (
  sessionId: string,
  reason: string,
): Promise<DRBFMSession> => {
  const response = await api.post<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/review-revisions`,
    { reason },
  );
  return mapSession(response.session ?? response);
};

// ==============================================================================
// Case Workflow
// ==============================================================================

export const createCase = async (
  params: DRBFMCaseInputData,
): Promise<DRBFMCase> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/cases`, {
    title: params.title,
    product_name: params.productName,
    target_part: params.targetPart,
    change_summary: params.changeSummary,
    source_document_ids: params.sourceDocumentIds || [],
    context: params.context || {},
  });

  return mapCase(response);
};

export const extractCaseChanges = async (
  caseId: string,
): Promise<DRBFMCaseDesignChange[]> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/cases/${caseId}/extract-changes`);
  return (response.design_changes || []).map(mapCaseDesignChange);
};

export const saveCaseDesignChanges = async (
  caseId: string,
  changes: DRBFMCaseDesignChange[],
): Promise<DRBFMCaseDesignChange[]> => {
  const response = await api.put<any>(`${DRBFM_BASE_URL}/cases/${caseId}/design-changes`, {
    design_changes: changes.map(caseDesignChangeToRequest),
  });
  return (response.design_changes || []).map(mapCaseDesignChange);
};

export const generateCasePrecheck = async (
  caseId: string,
): Promise<DRBFMCasePrecheckItem[]> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/cases/${caseId}/precheck`);
  return (response.precheck_items || []).map(mapCasePrecheckItem);
};

export const saveCasePrecheckAnswers = async (
  caseId: string,
  items: DRBFMCasePrecheckItem[],
): Promise<DRBFMCasePrecheckItem[]> => {
  const response = await api.put<any>(`${DRBFM_BASE_URL}/cases/${caseId}/precheck/answers`, {
    answers: items.map(precheckAnswerToRequest),
  });
  const precheckItems = response.case?.precheck_items;
  if (!Array.isArray(precheckItems)) {
    throw new Error(
      'DRBFM case precheck save response did not include case.precheck_items array.',
    );
  }
  return precheckItems.map(mapCasePrecheckItem);
};

export const analyzeCase = async (
  caseId: string,
): Promise<{ caseId: string; sessionId: string; status: string }> => {
  const response = await api.post<any>(`${DRBFM_BASE_URL}/cases/${caseId}/analyze`);
  const sessionId = firstPresent(
    response.session?.id,
    response.session_id,
    response.case?.linked_session_id,
    response.case?.linkedSessionId,
  );

  if (!sessionId) {
    throw new Error('DRBFM case analysis response did not include a session id.');
  }

  return {
    caseId: firstPresent(response.case?.id, response.case_id) || caseId,
    sessionId,
    status: firstPresent(response.case?.status, response.session?.status) || '',
  };
};

export const getCaseResults = async (
  caseId: string,
): Promise<SessionResultsResponse> => {
  const response = await api.get<any>(`${DRBFM_BASE_URL}/cases/${caseId}/results`);
  return mapSessionResults(response);
};

// ==============================================================================
// Analysis Execution (SSE Streaming)
// ==============================================================================

/**
 * Progress and error copy is user-visible, so the caller threads its own `t`.
 * Omitting it keeps the historical literals.
 */
const localize = (
  t: TFunction | undefined,
  key: string,
  fallback: string,
  options?: TranslationFallbackOptions,
): string => (t ? translateWithFallback(t, key, fallback, options) : fallback);

export const buildAnalyzeUrl = (
  sessionId: string,
  request: Pick<AnalyzeRequest, 'cadModelId'> = {},
): string => {
  const baseURL = process.env.REACT_APP_API_URL || '';
  const params = new URLSearchParams();
  if (request.cadModelId) {
    params.set('cad_model_id', request.cadModelId);
  }
  const query = params.toString();
  return `${baseURL}${DRBFM_BASE_URL}/sessions/${sessionId}/analyze${query ? `?${query}` : ''}`;
};

/**
 * Alternative: Start analysis using POST with fetch (for large specifications)
 * This handles the case where specifications are too large for URL parameters
 */
export const startAnalysisWithFetch = async (
  sessionId: string,
  request: AnalyzeRequest,
  callbacks: AnalysisCallbacks,
  abortSignal?: AbortSignal,
  t?: TFunction,
): Promise<void> => {
  const token = sessionStorage.getItem('access_token');

  try {
    const response = await fetchWithSession(
      buildAnalyzeUrl(sessionId, request),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(analyzeRequestBody(request)),
        signal: abortSignal,
      }
    );

    // Reported directly rather than thrown: the copy is already localized, and
    // routing it through the catch below would flatten it to the generic
    // analysis-failed fallback.
    if (!response.ok) {
      const detail = await response.json().then((body) => body?.detail, () => undefined);
      const code = typeof detail === 'string' ? detail : detail?.code;
      callbacks.onError(t ? analysisFailureMessage(t, detail)
        : `HTTP error: ${response.status}`, typeof code === 'string' ? code : undefined);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError(localize(t, 'drbfm.errors.noResponseBody', 'No response body'));
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let terminal = false;
    try {
      while (!terminal) {
        const { done, value } = await reader.read();
        if (abortSignal?.aborted) return;
        buffer = (buffer + decoder.decode(value, { stream: !done })).replace(/\r\n/g, '\n');
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        // A final event may end at EOF without a blank line.
        if (done && buffer.trim()) frames.push(buffer);
        for (const frame of frames) {
          const payload = frame.split('\n').filter((line) => line.startsWith('data:'))
            .map((line) => line.slice(5).trimStart()).join('\n');
          if (!payload) continue;
          const data = JSON.parse(payload);
          if (!data || typeof data.type !== 'string') throw new Error('invalid_analysis_event');
          terminal = ['complete', 'no_changes_found', 'error'].includes(data.type);
          await handleProgressEvent(data, callbacks, t);
          if (terminal || abortSignal?.aborted) break;
        }
        if (done) break;
      }
      if (!terminal && !abortSignal?.aborted) {
        callbacks.onError(localize(t, 'drbfm.firstRun.streamInterrupted', 'Analysis was interrupted. Retry with your saved input.'));
      }
    } finally {
      await reader.cancel().catch(() => undefined);
      reader.releaseLock();
    }
  } catch (error) {
    if (abortSignal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) return;
    // Only transport failures reach here now, and their messages are English
    // regardless of locale.
    callbacks.onError(localize(t, 'drbfm.errors.analysisFailed', 'Analysis failed'));
  }
};

/**
 * Handle progress event from fetch-based SSE
 */
export const handleProgressEvent = async (
  data: any,
  callbacks: AnalysisCallbacks,
  t?: TFunction,
): Promise<void> => {
  switch (data.type) {
    case 'connected':
      console.log('DRBFM analysis connected');
      break;

    case 'flow_start':
    case 'flow_progress':
    case 'flow_complete':
      callbacks.onProgress({
        type: data.type,
        flow: data.flow,
        message: data.message,
        progress: data.progress,
        data: data.data,
        timestamp: data.timestamp,
      });
      break;

    case 'cad_link_complete': {
      const linkedCount = Number(data.linked_count || 0);
      const unresolvedFailureModeIds = data.unresolved_failure_mode_ids || [];
      const unresolvedCount = unresolvedFailureModeIds.length;
      callbacks.onProgress({
        type: 'cad_link_complete',
        flow: 5,
        message: unresolvedCount > 0
          ? localize(
            t,
            'drbfm.cadLink.linkCompleteWithUnresolved',
            `CADリンク完了: ${linkedCount}件リンク、${unresolvedCount}件未解決`,
            { linked: linkedCount, unresolved: unresolvedCount },
          )
          : localize(
            t,
            'drbfm.cadLink.linkComplete',
            `CADリンク完了: ${linkedCount}件リンク`,
            { linked: linkedCount },
          ),
        progress: 0.98,
        data: {
          linkedCount,
          unresolvedFailureModeIds,
        },
        timestamp: data.timestamp || new Date().toISOString(),
      });
      break;
    }

    case 'cad_link_failed': {
      const errorCode = data.error_code || 'unknown';
      callbacks.onProgress({
        type: 'cad_link_failed',
        flow: 5,
        message: localize(
          t,
          'drbfm.cadLink.linkFailedWithCode',
          `CADリンクに失敗しました: ${errorCode}`,
          { code: errorCode },
        ),
        progress: 0.98,
        data: { errorCode },
        timestamp: data.timestamp || new Date().toISOString(),
      });
      break;
    }

    case 'no_changes_found':
    case 'complete':
      callbacks.onProgress({
        type: data.type,
        flow: 5,
        message: data.type === 'no_changes_found'
          ? localize(t, 'drbfm.firstRun.noChanges', 'Analysis completed. No changes were found.')
          : data.message || localize(t, 'drbfm.success.analysisComplete', 'Analysis completed'),
        progress: 1.0,
        data: data.data,
        timestamp: data.timestamp,
      });
      await callbacks.onComplete(data.type);
      break;

    case 'error': {
      const code = data.data?.code ?? data.code ?? data.error_code;
      const message = t && code ? analysisFailureMessage(t, code)
        : localize(t, 'drbfm.errors.analysisFailed', 'Analysis failed');
      if (typeof code === 'string') callbacks.onError(message, code);
      else callbacks.onError(message);
      break;
    }
  }
};

// ==============================================================================
// Export
// ==============================================================================

/**
 * Export session results as HTML
 * Returns a Blob with correct content type
 */
export const exportSessionAsHTML = async (
  sessionId: string,
  mode: 'draft' | 'reviewed' | 'scored' = 'draft',
): Promise<Blob> => {
  const baseURL = process.env.REACT_APP_API_URL || '';
  const token = sessionStorage.getItem('access_token');

  const response = await fetchWithSession(
    `${baseURL}${DRBFM_BASE_URL}/sessions/${sessionId}/export?mode=${mode}`,
    {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );

  if (!response.ok) {
    throw Object.assign(new Error('Export failed'), {
      code: `HTTP_${response.status}`, details: { http_status: response.status },
    });
  }

  // Get the blob and ensure it has the correct type
  const blob = await response.blob();

  // Re-create blob with explicit type if needed
  if (blob.type !== 'text/html') {
    return new Blob([blob], { type: 'text/html' });
  }

  return blob;
};

/**
 * Download exported HTML file
 */
export const downloadExportedHTML = async (
  sessionId: string,
  filename?: string,
  mode: 'draft' | 'reviewed' | 'scored' = 'draft',
): Promise<void> => {
  const blob = await exportSessionAsHTML(sessionId, mode);

  // Ensure filename has .html extension
  const safeFilename = sanitizeFilename(filename || `drbfm_${sessionId}.html`);

  // Create blob URL
  const blobUrl = URL.createObjectURL(blob);

  // Create and trigger download
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = safeFilename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);

  // Delay revoking to ensure download starts
  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 1000);
};

/**
 * Export session results as Excel
 * Returns a Blob with correct content type
 */
export const exportSessionAsExcel = async (
  sessionId: string,
  mode: 'draft' | 'reviewed' | 'scored' = 'draft',
): Promise<Blob> => {
  const baseURL = process.env.REACT_APP_API_URL || '';
  const token = sessionStorage.getItem('access_token');

  const response = await fetchWithSession(
    `${baseURL}${DRBFM_BASE_URL}/sessions/${sessionId}/export/excel?mode=${mode}`,
    {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );

  if (!response.ok) {
    throw Object.assign(new Error('Export failed'), {
      code: `HTTP_${response.status}`, details: { http_status: response.status },
    });
  }

  return response.blob();
};

/**
 * Download exported Excel file
 */
export const downloadExportedExcel = async (
  sessionId: string,
  filename?: string,
  mode: 'draft' | 'reviewed' | 'scored' = 'draft',
): Promise<void> => {
  const blob = await exportSessionAsExcel(sessionId, mode);

  // Ensure filename has .xlsx extension
  let safeFilename = filename || `drbfm_${sessionId}.xlsx`;
  if (!safeFilename.toLowerCase().endsWith('.xlsx')) {
    safeFilename = `${safeFilename}.xlsx`;
  }
  safeFilename = safeFilename.replace(/[<>:"/\\|?*]/g, '_');

  // Create blob URL
  const blobUrl = URL.createObjectURL(blob);

  // Create and trigger download
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = safeFilename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);

  // Delay revoking to ensure download starts
  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 1000);
};

/**
 * Get HTML preview content for a session
 */
export const getSessionHTMLPreview = async (sessionId: string): Promise<string> => {
  const response = await api.get<{ html: string }>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/preview`
  );
  return response.html;
};

// ==============================================================================
// DRBFM → Verification Conversion
// ==============================================================================

/**
 * Convert DRBFM failure modes to verification items
 */
export const convertToVerification = async (
  sessionId: string,
  request: ConvertToVerificationRequest,
): Promise<ConvertToVerificationResponse> => {
  const response = await api.post<any>(
    `${DRBFM_BASE_URL}/sessions/${sessionId}/convert-to-verification`,
    {
      failure_mode_ids: request.failureModeIds,
      verification_session_id: request.verificationSessionId || null,
      verification_unit_id: request.verificationUnitId || null,
      new_session_title: request.newSessionTitle || null,
      new_session_model_name: request.newSessionModelName || null,
      new_unit_number: request.newUnitNumber || null,
      new_unit_name: request.newUnitName || null,
    },
  );

  return {
    verificationSessionId: response.verification_session_id,
    verificationUnitId: response.verification_unit_id,
    itemsCreated: response.items_created,
    items: response.items || [],
  };
};

// ==============================================================================
// Service Object Export
// ==============================================================================

/**
 * Convert one failure mode into the shared evidence contract (#966, Story #928).
 *
 * The BACKEND is the single adapter: converting client-side would let two adapters
 * disagree about the same payload and break reproducibility. The endpoint is gated by
 * `ENABLE_REVY_EVIDENCE_ADAPTERS` and 404s when it is off, which the caller renders as a
 * distinct state rather than as "no evidence".
 */
async function convertFailureModeEvidence(
  failureMode: FailureMode,
  subjectRevisionId: string,
): Promise<LegacyConversion> {
  // `retrieved_at` is customer-visible provenance — when the knowledge search that produced
  // these references ran. Without the failure mode's creation time there is nothing honest
  // to send; the browser clock would stamp panel-open time onto every EvidenceRef.
  if (!failureMode.createdAt) {
    throw new Error('the failure mode carries no creation time to send as retrieved_at');
  }
  // `api.post` already unwraps to `res.data` (`services/api.ts:238-239`), so this IS the
  // body — reading `.data` off it yields undefined and the mapper's defaults would quietly
  // return an empty finding.
  const response = await api.post<any>('/evidence/legacy-conversions', {
    source_type: 'drbfm',
    payload: failureModeToLegacyPayload(failureMode),
    subject_revision_id: subjectRevisionId,
    retrieved_at: failureMode.createdAt,
  });
  return mapLegacyConversion(response);
}

export const drbfmService = {
  // Session management
  createSession,
  listSessions,
  getSession,
  getSessionResults,
  deleteSession,
  linkCad,

  // Staged workflow
  getWorkflow,
  configureWorkflow,
  getFunctionPartMatrix,
  importFunctionPartMatrix,
  generateFunctionPartMatrix,
  prefillFunctionPartMatrix,
  updateFunctionPartMatrixCell,
  promoteFunctionPartMatrix,
  createWorkflowArtifact,
  runWorkflowStage,
  updateDesignChangeReview,
  confirmComparability,
  updateFailureModeReview,
  previewFailureModeRevision,
  updateCountermeasureReview,
  getReadiness,
  createReviewRevision,

  // Case workflow
  createCase,
  extractCaseChanges,
  saveCaseDesignChanges,
  generateCasePrecheck,
  saveCasePrecheckAnswers,
  analyzeCase,
  getCaseResults,

  // Analysis
  startAnalysisWithFetch,
  buildAnalyzeUrl,

  // Export
  exportSessionAsHTML,
  downloadExportedHTML,
  exportSessionAsExcel,
  downloadExportedExcel,

  // Preview
  getSessionHTMLPreview,

  // Conversion
  convertToVerification,
  convertFailureModeEvidence,
};

export default drbfmService;
